import styled from "@emotion/styled"
import { useRef, useLayoutEffect } from "react"
import { useConsole } from "contexts/Console"
import { motion, useMotionValue, useMotionValueEvent, useTransform, useMotionTemplate } from "framer-motion"
import getMediaQuery from "css/breakpoints"
import { contrastMode } from "css/a11y"
import { SYNODIC_MONTH } from "./lunar"
import { waTriggerEvent } from "components/analytics/DigitalDataLayer"
import { useMoonCalendar, observeResize } from "./context"
import ImageCLD from "components/media/ImageCLD"
import { useMoonphaseCalendar } from "contexts/MoonphaseCalendar"
import imageLoader from "components/media/imageLoader"
import { useEnv } from "contexts/Env"

const LOADING_CLS = "mc-disc-moons"
const SWAP_CLS = "mc-swap"

const Main = styled(motion.div)`
  @property --mc-disc-angle-to {
    syntax: "<angle>";
    inherits: true;
    initial-value: 0deg;
  }
  --mc-disc-angle-reverse: calc(360deg - var(--mc-disc-angle-to));

  position: relative;
  grid-row: 2;
  grid-column: doc;
  height: 100%;
  width: 100%;
  display: grid;
  justify-items: center;
  align-items: start;
//  transform: scaleX(var(--mc-dir));
  transform-origin: 50% 50%;
  align-self: start;
  pointer-events: none;

  &.focus-visible {
    outline: none;
  }
`

const Wrapper = styled.div`
  @property --mc-disc-angle {
    syntax: "<angle>";
    inherits: true;
    initial-value: 0deg;
  }

  display: grid;
  justify-items: center;
  transform-origin: 50% 50%;
  position: absolute;
  height: 100%;
  aspect-ratio: 1;

  transform: rotate(calc(var(--mc-disc-angle-reverse) - 120deg)) scale(.7);
  opacity: 0;
  transition: opacity 1s cubic-bezier(0.32, 0, 0.67, 0),
    transform 3s cubic-bezier(0.22, 1, 0.36, 1);

  html.prefers-reduced-motion & {
    transition-duration: 0s, 0s;
  }

  img {
    position: absolute;
    grid-row: 1;
    grid-column: 1;
    display: block;
    width: 100%;
    height: 100%;
  }

  .mc-loaded & {
    opacity: 1;
    transform: rotate(var(--mc-disc-angle-reverse)) scale(1);
  }

  .mc-ended & {
    transform: rotate(var(--mc-disc-angle-reverse));
    transition: none;
  }

  .${SWAP_CLS} & {
    transition: transform 1.2s cubic-bezier(0.33, 1, 0.68, 1);
  }

  ${getMediaQuery("s")} {
    height: 200%;
  }

  .focus-visible & {
    outline: 2px solid rgb(var(--focus, 0 255 255) / 1) !important;
    outline-offset: 3px;
    border-radius: 9999px;
  }
`

const Circles = styled(ImageCLD)`
  --mc-circles-scale: 0.7;
  transform: scale(var(--mc-circles-scale));
  opacity: 0;
  transition: transform 2s cubic-bezier(0.22, 1, 0.36, 1) 1.5s;

  html.prefers-reduced-motion & {
    transition-duration: 0s;
    transition-delay: 0s;
  }

  .mc-loaded & {
    opacity: 1;
    --mc-circles-scale: 1;
  }
  .mc-ended & {
    z-index: 1;
  }
`

const Ticks = styled(ImageCLD)`
  --mc-ticks-angle: -90deg;
  --mc-ticks-alpha: 0;
  transform: rotate(var(--mc-ticks-angle));
  opacity: 0;
  transition: transform 2s cubic-bezier(0.22, 1, 0.36, 1) 1.5s, opacity 1s cubic-bezier(1, 1, 1, 1) 2s;

  html.prefers-reduced-motion & {
    transition-duration: 0s;
    transition-delay: 0s;
  }

  .mc-loaded & {
    --mc-ticks-angle: 0deg;
    opacity: 1;
  }
`

const Moons = styled(ImageCLD)`
  opacity: var(--alpha-swap);
`

const Arrow = styled.svg`
  --mc-arrow-scale: 0;
  display: block;
  position: absolute;
  width: auto;

  @media (orientation: portrait) {
    height: 2%;
  }
  @media (orientation: landscape) {
    height: 3%;
  }
  transform: scale(var(--mc-arrow-scale));
  transform-origin: 50% 0%;
  transition: transform 2s cubic-bezier(0.22, 1, 0.36, 1) 2.4s;

  html.prefers-reduced-motion & {
    transition-duration: 0s;
    transition-delay: 0s;
  }

  .mc-loaded & {
    --mc-arrow-scale: 1;
  }
`

const Phase = styled(motion.div)`
  position: absolute;
  aspect-ratio: 1;
  background-image: var(--bgasset);
  background-position: var(--mc-phase-pos);
  background-repeat: no-repeat;
  background-size: 600% 600%;

  transform: rotate(var(--mc-phase-angle));
  opacity: 0;
  transition: opacity 1s cubic-bezier(1, 1, 1, 1) 2.4s;

  html.prefers-reduced-motion & {
    transition-duration: 0s;
    transition-delay: 0s;
  }

  width: auto;

  top: 2.8%;
  height: 1.3%;
  ${getMediaQuery("s")} {
    top: 5.4%;
    height: 2.8%;
  }

  .mc-loaded & {
    opacity: 1;
  }
`

const PHASE_XY = {
  count: 31,
  num: 6,
  pad: 100 / (6 - 1),
}

function MCPhase(props) {
  const console = useConsole()
  const env = useEnv()

  const { ctx, sprite_cycle } = props

  const { moon_illu } = useMoonCalendar(ctx)
  const { hemisphere } = useMoonphaseCalendar()

  const getUrl = imageLoader({ src: sprite_cycle[0] }, env)

  const phase_pos = useTransform(moon_illu, v => {
    const pos = Math.floor((PHASE_XY.count * v) / 2)
    const x = (pos % PHASE_XY.num) * PHASE_XY.pad
    const y = Math.floor(pos / PHASE_XY.num) * PHASE_XY.pad
    return `${x}% ${y}%`
  })

  const phase_angle = useMotionValue(0)
  const phase_angle_deg = useMotionTemplate`${phase_angle}deg`

  useLayoutEffect(() => {
    phase_angle.set((hemisphere !== "south") * 180)
  }, [hemisphere])

  return (
    <Phase
      style={{
        "--bgasset": `url(${getUrl({ width: 576 })})`,
        "--mc-phase-pos": phase_pos,
        "--mc-phase-angle": phase_angle_deg,
      }}
    />
  )
}

export default function MoonCalendarDisc(props) {
  const console = useConsole()
  //  console.verbose("MoonCalendarDisc", props)
  const { ctx, dial_bg, lines, dashes, heading } = props
  const { loaded, moon_phase, moon_time, px_ratio, px_ratio_p, moving, focused } = useMoonCalendar(ctx)

  const rfmain = useRef(null)
  const rfwrapper = useRef(null)

  function onLoad(e) {
    if (e?.target?.classList?.contains(LOADING_CLS)) return
    loaded.set(1)
  }

  useLayoutEffect(() => {
    loaded.set(+rfmain.current?.querySelector?.(`.${LOADING_CLS}`)?.complete)
  }, [])

  function angleFromPhase([v, moving]) {
    let next_angle = ((v + 1) * 180) % 360
    if (time_dir.current < 0 && last_angle.current >= next_angle) next_angle += 360
    if (time_dir.current > 0 && last_angle.current <= next_angle) next_angle -= 360

    if (Math.abs(last_angle.current - next_angle) > 360) {
      if (time_dir.current < 0) next_angle -= 360
      if (time_dir.current > 0) next_angle += 360
    }

    last_angle.current = next_angle
    time_dir.current = 0
    return next_angle
  }

  function roundedAngle(v) {
    return parseFloat(v.toFixed(2))
  }

  const time_dir = useRef(0)
  const last_angle = useRef(0)
  const angle_raw = useTransform([moon_phase, moving], angleFromPhase)
  const angle = useTransform(angle_raw, roundedAngle)
  const angle_deg = useMotionTemplate`${angle}deg`

  const scale = useMotionValue(0)
  const top = useMotionValue(0)

  function onResize({ isize, target }) {
    const el = target.querySelector("img")
    const { offsetWidth: w = 0, offsetHeight: h = 0 } = el
    if (!w * h) return
    const W = parseFloat(getComputedStyle(target).getPropertyValue("--mc-disc-w"))
    const H = target.offsetTop
    const s = W / w
    scale.set(s)
    top.set(H + (h * s) / 2)
    const ratio = SYNODIC_MONTH * 24 * 60 * 60 * 1000
    px_ratio.current = (ratio * 0.75) / w
    px_ratio_p.current = ratio * 0.75 * 2.2
  }
  useLayoutEffect(() => observeResize(rfmain.current, onResize))

  function onMoving(v) {
    rfmain.current.classList.remove(SWAP_CLS)
    if (!v) return
    rfmain.current.classList.add(SWAP_CLS)
    const current = moon_time.get()
    time_dir.current = current > v ? -1 : 1
    last_angle.current = angle_raw.get()
    moon_time.set(v)
  }
  useMotionValueEvent(moving, "change", onMoving)

  function onFocus(e) {
    focused.set(1)
    moving.set(0)
  }

  function onBlur(e) {
    focused.set(0)
  }

  function onKeyDown(e) {
    let dir = ["ArrowLeft", "ArrowRight"].indexOf(e.code)
    if (dir < 0) return
    e.preventDefault()
    dir = 1 - 2 * dir
    const current_moon_time = moon_time.get()
    moon_time.set(current_moon_time + dir * px_ratio.current * 8)
  }

  return (
    <Main
      style={{
        "--mc-disc-scale": scale,
        "--mc-disc-top": top,
        "--mc-disc-angle-to": angle_deg,
      }}
      ref={rfmain}
      onLoad={onLoad}
      tabIndex='0'
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
    >
      <Wrapper ref={rfwrapper}>
        <Circles sources={lines} sizes='auto' alt='' />
        <Ticks sources={dashes} sizes='auto' alt='' />
        <Moons sources={dial_bg} sizes='auto' alt={heading.title} />
      </Wrapper>
      <Arrow width='35' height='30' viewBox='0 0 35 30' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path d='M17.3203 30.0006L-0.000195742 0.000576806L17.3203 0.00057832L34.6408 0.000579834L17.3203 30.0006Z' fill='white' />
        <path d='M17.3201 30.0006L34.6406 0.000579834L17.3201 0.00057832L17.3201 30.0006Z' fill='#DADADA' />
      </Arrow>
      <MCPhase {...props} />
    </Main>
  )
}
